import { supabase } from "./supabase";

/**
 * Optimized Individual Affiliate Service
 * Uses server-side functions for consistent data with dashboard
 * Includes caching for improved performance
 */

// Cache implementation
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
  });
  console.log(`💾 Cached data for ${key}`);
};

const clearCache = () => {
  cache.clear();
  console.log("🗑️ Individual affiliate cache cleared");
};

/**
 * Get comprehensive performance summary for individual affiliate
 * Uses same calculations as dashboard for consistency
 */
export const getIndividualAffiliatePerformance = async (
  affiliateName,
  brand = null,
  startDate = null,
  endDate = null,
  retryCount = 0
) => {
  const maxRetries = 2;
  const timeoutMs = 15000; // 15 second timeout

  try {
    const cacheKey = `individual_performance_${affiliateName}_${brand || "all"}_${startDate || "all"}_${endDate || "all"}`;

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return cachedData;

    console.log(
      `🚀 Loading individual affiliate performance for ${affiliateName} (attempt ${retryCount + 1}/${maxRetries + 1})`
    );

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Database query timeout after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    // Create the database query promise - use fast function with fixed commission calculation
    const queryPromise = supabase.rpc(
      "get_individual_affiliate_performance_fast",
      {
        p_affiliate_name: affiliateName,
        brand_filter: brand === "all" ? null : brand,
        start_date: startDate,
        end_date: endDate,
      }
    );

    // Race between query and timeout
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

    if (error) {
      console.error(
        "❌ Error loading individual affiliate performance:",
        error
      );
      throw error;
    }

    console.log(`✅ Individual affiliate performance loaded:`, data[0]);

    const result = data[0] || {
      affiliate_name: affiliateName,
      total_ad_spend: 0,
      total_revenue: 0,
      total_commission: 0,
      total_profit: 0,
      total_purchases: 0,
      ad_count: 0,
      avg_roas: 0,
      best_ad_name: "No ads found",
      best_ad_spend: 0,
      best_ad_revenue: 0,
      worst_performing_roas: 0,
      total_days_active: 0,
      avg_daily_spend: 0,
      commission_tier_label: "No data",
    };

    // Cache the result
    setCachedData(cacheKey, result);

    return result;
  } catch (error) {
    console.error("❌ Error in getIndividualAffiliatePerformance:", error);

    // Check if it's a timeout or database error
    const isTimeoutError =
      error.message?.includes("timeout") || error.code === "57014";

    if (isTimeoutError && retryCount < maxRetries) {
      console.log(
        `🔄 Retrying in 2 seconds... (${retryCount + 1}/${maxRetries})`
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return getIndividualAffiliatePerformance(
        affiliateName,
        brand,
        startDate,
        endDate,
        retryCount + 1
      );
    }

    // If all retries failed, return fallback data
    if (retryCount >= maxRetries) {
      console.log(
        `🚨 All retries failed, returning fallback data for ${affiliateName}`
      );
      return {
        affiliate_name: affiliateName,
        total_ad_spend: 0,
        total_revenue: 0,
        total_commission: 0,
        total_profit: 0,
        total_purchases: 0,
        ad_count: 0,
        avg_roas: 0,
        best_ad_name: "Data temporarily unavailable",
        best_ad_spend: 0,
        best_ad_revenue: 0,
        worst_performing_roas: 0,
        total_days_active: 0,
        avg_daily_spend: 0,
        commission_tier_label: "Unavailable",
        isTemporaryData: true,
      };
    }

    throw error;
  }
};

/**
 * Get detailed ad performance for individual affiliate
 * Returns individual ad records with consistent commission calculations
 */
export const getIndividualAffiliateAds = async (
  affiliateName,
  brand = null,
  startDate = null,
  endDate = null,
  limit = 500,
  retryCount = 0
) => {
  const maxRetries = 2;
  const timeoutMs = 20000; // 20 second timeout for ads (larger dataset)

  try {
    const cacheKey = `individual_ads_${affiliateName}_${brand || "all"}_${startDate || "all"}_${endDate || "all"}_${limit}`;

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return cachedData;

    console.log(
      `🚀 Loading individual affiliate ads for ${affiliateName} (limit: ${limit}, attempt ${retryCount + 1}/${maxRetries + 1})`
    );

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Database query timeout after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    // Create the database query promise - use fast function for better performance
    const queryPromise = supabase.rpc("get_individual_affiliate_ads_fast", {
      p_affiliate_name: affiliateName,
      brand_filter: brand === "all" ? null : brand,
      start_date: startDate,
      end_date: endDate,
      limit_count: limit,
      offset_count: 0,
    });

    // Race between query and timeout
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

    if (error) {
      console.error("❌ Error loading individual affiliate ads:", error);
      throw error;
    }

    console.log(`✅ Individual affiliate ads loaded: ${data.length} records`);

    // Transform data to expected format
    const transformedData = data.map((ad) => ({
      adName: ad.ad_name,
      campaignName: ad.campaign_name,
      adsetName: ad.adset_name,
      date: ad.date,
      adSpend: parseFloat(ad.spend) || 0,
      revenue: parseFloat(ad.revenue) || 0,
      roas: parseFloat(ad.roas) || 0,
      commission: parseFloat(ad.commission) || 0,
      profit: parseFloat(ad.profit) || 0,
      purchases: parseInt(ad.purchases) || 0,
      clicks: parseInt(ad.clicks) || 0,
      impressions: parseInt(ad.impressions) || 0,
      cpa: parseFloat(ad.cpa) || 0,
      aov: parseFloat(ad.aov) || 0,
      brand: ad.brand || "",
      commissionTier: ad.commission_tier || "Below Tier",
      // Additional fields for compatibility
      "Ad Name": ad.ad_name,
      "Campaign Name": ad.campaign_name,
      "Adset Name": ad.adset_name,
      "Ad Spend": parseFloat(ad.spend) || 0,
      "Pixel CV": parseFloat(ad.revenue) || 0,
      ROAS: parseFloat(ad.roas) || 0,
      Commission: parseFloat(ad.commission) || 0,
      "Pixel Purchases": parseInt(ad.purchases) || 0,
      CPA: parseFloat(ad.cpa) || 0,
      AOV: parseFloat(ad.aov) || 0,
      CommissionTier: ad.commission_tier || "Below Tier",
      CommissionLabel: ad.commission_tier || "Below Tier",
      Brand: ad.brand || "",
      Date: ad.date,
    }));

    // Cache the result
    setCachedData(cacheKey, transformedData);

    return transformedData;
  } catch (error) {
    console.error("❌ Error in getIndividualAffiliateAds:", error);

    // Check if it's a timeout or database error
    const isTimeoutError =
      error.message?.includes("timeout") || error.code === "57014";

    if (isTimeoutError && retryCount < maxRetries) {
      console.log(
        `🔄 Retrying ads query in 3 seconds... (${retryCount + 1}/${maxRetries})`
      );
      await new Promise((resolve) => setTimeout(resolve, 3000));
      return getIndividualAffiliateAds(
        affiliateName,
        brand,
        startDate,
        endDate,
        limit,
        retryCount + 1
      );
    }

    // If all retries failed, return empty array
    if (retryCount >= maxRetries) {
      console.log(
        `🚨 All retries failed for ads, returning empty array for ${affiliateName}`
      );
      return [];
    }

    throw error;
  }
};

/**
 * Get daily performance data for charts
 * Returns time-series data for visualization
 */
export const getIndividualAffiliateDailyPerformance = async (
  affiliateName,
  brand = null,
  daysBack = 30
) => {
  try {
    const cacheKey = `individual_daily_${affiliateName}_${brand || "all"}_${daysBack}`;

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return cachedData;

    console.log(
      `🚀 Loading daily performance for ${affiliateName} (${daysBack} days)`
    );

    const { data, error } = await supabase.rpc(
      "get_individual_affiliate_daily_performance",
      {
        p_affiliate_name: affiliateName,
        brand_filter: brand === "all" ? null : brand,
        days_back: daysBack,
      }
    );

    if (error) {
      console.error("❌ Error loading daily performance:", error);
      throw error;
    }

    console.log(`✅ Daily performance loaded: ${data.length} days`);

    // Transform data for chart compatibility
    const chartData = data.map((day) => ({
      date: day.date,
      spend: parseFloat(day.daily_spend) || 0,
      revenue: parseFloat(day.daily_revenue) || 0,
      commission: parseFloat(day.daily_commission) || 0,
      profit: parseFloat(day.daily_profit) || 0,
      purchases: parseInt(day.daily_purchases) || 0,
      roas: parseFloat(day.daily_roas) || 0,
      adCount: parseInt(day.ad_count) || 0,
    }));

    // Cache the result
    setCachedData(cacheKey, chartData);

    return chartData;
  } catch (error) {
    console.error("❌ Error in getIndividualAffiliateDailyPerformance:", error);
    throw error;
  }
};

/**
 * Get formatted data for affiliate page (combines summary and ads)
 * Returns data in the format expected by existing components
 */
export const getFormattedAffiliateData = async (
  affiliateName,
  brand = null,
  startDate = null,
  endDate = null
) => {
  try {
    console.log(`🚀 Loading formatted affiliate data for ${affiliateName}`);

    // Load summary data first (faster, more critical)
    const performanceData = await getIndividualAffiliatePerformance(
      affiliateName,
      brand,
      startDate,
      endDate
    );

    // Load ads data separately with smaller limit for faster loading
    let adsData = [];
    try {
      console.log(
        `🔄 Loading ads data for ${affiliateName} with progressive loading...`
      );
      adsData = await getIndividualAffiliateAds(
        affiliateName,
        brand,
        startDate,
        endDate,
        200
      ); // Smaller limit
    } catch (adsError) {
      console.warn(
        `⚠️ Failed to load ads data for ${affiliateName}, continuing with summary only:`,
        adsError
      );
      // Continue with empty ads data - summary is more important
    }

    // Format in the structure expected by existing components
    const formattedData = {
      name: affiliateName,
      totalAdSpend: parseFloat(performanceData.total_ad_spend) || 0,
      totalRevenue: parseFloat(performanceData.total_revenue) || 0,
      totalCommission: parseFloat(performanceData.total_commission) || 0,
      totalProfit: parseFloat(performanceData.total_profit) || 0,
      totalPurchases: parseInt(performanceData.total_purchases) || 0,
      adCount: parseInt(performanceData.ad_count) || 0,
      avgROAS: parseFloat(performanceData.avg_roas) || 0,

      // Additional analytics
      bestAdName: performanceData.best_ad_name,
      bestAdSpend: parseFloat(performanceData.best_ad_spend) || 0,
      bestAdRevenue: parseFloat(performanceData.best_ad_revenue) || 0,
      worstROAS: parseFloat(performanceData.worst_performing_roas) || 0,
      totalDaysActive: parseInt(performanceData.total_days_active) || 0,
      avgDailySpend: parseFloat(performanceData.avg_daily_spend) || 0,
      commissionTierLabel: performanceData.commission_tier_label,

      // Legacy fields for compatibility
      avgCommissionRate:
        performanceData.total_ad_spend > 0
          ? performanceData.total_commission / performanceData.total_ad_spend
          : 0,

      // Individual ads data
      ads: adsData,

      // Empty objects for compatibility with existing code
      commissionBreakdown: {},
      brand: brand || "",
    };

    console.log(`✅ Formatted affiliate data loaded:`, {
      name: formattedData.name,
      totalCommission: formattedData.totalCommission,
      adCount: formattedData.adCount,
      avgROAS: formattedData.avgROAS,
    });

    return formattedData;
  } catch (error) {
    console.error("❌ Error in getFormattedAffiliateData:", error);
    throw error;
  }
};

/**
 * Clear cache for individual affiliate data
 */
export const clearIndividualAffiliateCache = () => {
  clearCache();
};

// Export individual functions
export const individualAffiliateService = {
  getIndividualAffiliatePerformance,
  getIndividualAffiliateAds,
  getIndividualAffiliateDailyPerformance,
  getFormattedAffiliateData,
  clearIndividualAffiliateCache,
};
